'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMicroSource, type MicroContentItem } from '@akademi/site-builder-runtime';
import { MicroPromptCard } from './MicroPromptCard';

/**
 * Renders micro items attached to (course, lesson) one by one as
 * inline {@link MicroPromptCard}s. The host supplies the {@code
 * attachments} array; this component owns the advance/skip flow.
 *
 * <p>{@code onAllComplete} fires when every attached check has been
 * answered or skipped. A persistent "Skip all" button respects the
 * "never hard-block" principle.
 */
export interface MicroPostLessonCheckProps {
  /** Item IDs attached to this lesson. Hosts resolve which items
   *  belong to which (course, lesson) via their own backend; the
   *  contract here is just "render these item ids in order". */
  attachmentIds: Array<string | number>;
  onAllComplete?: () => void;
  contextLabel?: string;
}

export function MicroPostLessonCheck({
  attachmentIds,
  onAllComplete,
  contextLabel: authoredContextLabel,
}: MicroPostLessonCheckProps) {
  const { t } = useTranslation('site-builder-blocks');
  const contextLabel = authoredContextLabel ?? t('microPostLessonCheck.contextLabel');
  const micro = useMicroSource();
  const [items, setItems] = useState<MicroContentItem[] | null>(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (attachmentIds.length === 0) {
      onAllComplete?.();
      return;
    }
    if (!micro) return;
    let cancelled = false;
    Promise.all(attachmentIds.map((id) => micro.content.fetchById(id))).then((resolved) => {
      if (cancelled) return;
      const present = resolved.filter((x): x is MicroContentItem => !!x);
      setItems(present);
      if (present.length === 0) onAllComplete?.();
    });
    return () => {
      cancelled = true;
    };
  }, [attachmentIds, micro, onAllComplete]);

  if (attachmentIds.length === 0) return null;
  if (!items) return null;
  if (idx >= items.length) return null;

  const advance = () => {
    if (idx >= items.length - 1) {
      onAllComplete?.();
      return;
    }
    setIdx((i) => i + 1);
  };

  const item = items[idx]!;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <MicroPromptCard
        item={item}
        contextLabel={`${contextLabel} · ${idx + 1} / ${items.length}`}
        onComplete={advance}
        onDismiss={advance}
      />
      <button
        type="button"
        onClick={() => onAllComplete?.()}
        style={{
          alignSelf: 'flex-end',
          background: 'none',
          border: 'none',
          fontSize: 'var(--font-size-xs, 12px)',
          color: 'var(--color-text-tertiary, #94867d)',
          cursor: 'pointer',
        }}
      >
        {t('microPostLessonCheck.skipAll')}
      </button>
    </div>
  );
}
